import React, { useState, useEffect } from "react";
import api from "../api";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { IconButton } from "@mui/material";

function UserAuth({ setUser }) {
  const [isSignup, setIsSignup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    label: "",
    color: "",
    width: "0%",
  });

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone_number: "",
    username: "",
    password: "",
    credential: "",
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    const username = localStorage.getItem("username");
    const user_id = localStorage.getItem("user_id");
    if (token && username && user_id) {
      setUser({ username, user_id, token });
    }
  }, [setUser]);

  const getPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[@$!%*?#&]/.test(password)) strength++;

    if (strength <= 2) return { label: "Weak", color: "#ef4444", width: "33%" };
    if (strength === 3 || strength === 4)
      return { label: "Medium", color: "#f59e0b", width: "66%" };
    if (strength === 5)
      return { label: "Strong", color: "#22c55e", width: "100%" };
    return { label: "", color: "", width: "0%" };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
    if (name === "password") setPasswordStrength(getPasswordStrength(value));
  };

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "firstName":
        if (!value.trim()) error = "First name is required";
        break;
      case "email":
        if (!value.trim()) error = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(value)) error = "Invalid email format";
        break;
      case "phone_number":
        if (!value.trim()) error = "Phone number is required";
        else if (!/^[0-9]{10}$/.test(value))
          error = "Phone number must be exactly 10 digits";
        break;
      case "username":
        if (!value.trim()) error = "Username is required";
        break;
      case "password":
        if (!value.trim()) error = "Password is required";
        else if (
          !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?#&])[A-Za-z\d@$!%*?#&]{8,}$/.test(
            value,
          )
        )
          error =
            "Password must be 8+ chars, include uppercase, lowercase, number & special char";
        break;
      case "credential":
        if (!value.trim()) error = "Please enter email, phone, or username";
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validateForm = () => {
    const currentErrors = {};
    const requiredFields = isSignup
      ? ["firstName", "email", "phone_number", "username", "password"]
      : ["credential", "password"];

    requiredFields.forEach((field) => {
      validateField(field, formData[field]);
      if (!formData[field] || errors[field]) {
        currentErrors[field] = errors[field] || "This field is required";
      }
    });

    setErrors(currentErrors);
    return Object.keys(currentErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!validateForm()) {
      setMessage("Please fix the errors before submitting");
      return;
    }

    setLoading(true);
    try {
      if (isSignup) {
        const res = await api.post(`/auth/user-signup`, formData);
        setMessage(res.data.message || "Signup successful!");
        localStorage.setItem("username", res.data.username);
        localStorage.setItem("user_id", res.data.user_id);
        setUser({ username: res.data.username, user_id: res.data.user_id });
      } else {
        const res = await api.post(`/auth/login`, {
          credential: formData.credential,
          password: formData.password,
        });
        localStorage.setItem("token", res.data.token);
        localStorage.setItem("username", res.data.username);
        localStorage.setItem("user_id", res.data.user_id);
        setMessage(res.data.message || "Login successful!");
        setUser({
          username: res.data.username,
          user_id: res.data.user_id,
          token: res.data.token,
        });
      }
    } catch (error) {
      console.error("Auth Error:", error);
      setMessage(error.response?.data?.error || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone_number: "",
      username: "",
      password: "",
      credential: "",
    });
    setErrors({});
    setMessage("");
    setPasswordStrength({ label: "", color: "", width: "0%" });
    setShowPassword(false);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",

        background: `
        radial-gradient(circle at 20% 20%, rgba(25,118,210,0.15), transparent 40%),
        radial-gradient(circle at 80% 30%, rgba(0,200,83,0.12), transparent 40%),
        radial-gradient(circle at 50% 80%, rgba(255,82,82,0.10), transparent 40%),
        linear-gradient(135deg, #f5f7fa, #e4ecf7)
      `,
      }}
    >
      <div
        style={{
          width: 380,
          maxWidth: "95%",
          padding: "40px 32px",
          borderRadius: 20,

          background: "rgba(255,255,255,0.6)",
          backdropFilter: "blur(18px)",

          border: "1px solid rgba(255,255,255,0.4)",

          boxShadow: "0 12px 40px rgba(0,0,0,0.15)",

          transition: "all 0.25s ease",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            fontSize: 26,
            fontWeight: 700,
            marginBottom: 6,
            color: "#073057",
          }}
        >
          {isSignup ? "Create Account" : "Welcome Back"}
        </h2>

        <p
          style={{
            textAlign: "center",
            fontSize: 14,
            color: "#6b7280",
            marginBottom: 28,
          }}
        >
          {isSignup
            ? "Sign up to get started with your account."
            : "Login to access your dashboard."}
        </p>

        <form onSubmit={handleSubmit}>
          {isSignup ? (
            <>
              <InputField
                {...{
                  label: "First Name",
                  name: "firstName",
                  value: formData.firstName,
                  onChange: handleChange,
                  error: errors.firstName,
                }}
              />
              <InputField
                {...{
                  label: "Last Name",
                  name: "lastName",
                  value: formData.lastName,
                  onChange: handleChange,
                }}
              />
              <InputField
                {...{
                  label: "Email",
                  name: "email",
                  value: formData.email,
                  onChange: handleChange,
                  error: errors.email,
                }}
              />
              <InputField
                {...{
                  label: "Phone Number",
                  name: "phone_number",
                  value: formData.phone_number,
                  onChange: handleChange,
                  error: errors.phone_number,
                }}
              />
              <InputField
                {...{
                  label: "Username",
                  name: "username",
                  value: formData.username,
                  onChange: handleChange,
                  error: errors.username,
                }}
              />
            </>
          ) : (
            <InputField
              label="Email / Phone / Username"
              name="credential"
              value={formData.credential}
              onChange={handleChange}
              error={errors.credential}
            />
          )}

          {/* PASSWORD */}
          <div style={{ position: "relative", marginBottom: 10 }}>
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Password *"
              value={formData.password}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: 10,
                border: errors.password
                  ? "1px solid #ef4444"
                  : "1px solid rgba(255,255,255,0.5)",

                background: "rgba(255,255,255,0.6)",
                backdropFilter: "blur(10px)",

                outline: "none",
                fontSize: 14,
              }}
            />

            <IconButton
              onClick={() => setShowPassword(!showPassword)}
              sx={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </div>

          {errors.password && (
            <span style={{ color: "#ef4444", fontSize: 12 }}>
              {errors.password}
            </span>
          )}

          {/* PASSWORD STRENGTH */}
          {isSignup && formData.password && (
            <div style={{ marginTop: 10 }}>
              <div
                style={{
                  height: 6,
                  borderRadius: 6,
                  background: "#eee",
                }}
              >
                <div
                  style={{
                    height: 6,
                    width: passwordStrength.width,
                    background: passwordStrength.color,
                    borderRadius: 6,
                    transition: "0.3s",
                  }}
                />
              </div>

              <p
                style={{
                  fontSize: 12,
                  marginTop: 4,
                  color: passwordStrength.color,
                }}
              >
                {passwordStrength.label} password
              </p>
            </div>
          )}

          {/* SUBMIT */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              marginTop: 20,
              padding: "12px",
              borderRadius: 999,
              border: "none",

              background:
                "linear-gradient(135deg, rgba(25,118,210,0.9), rgba(25,118,210,0.6))",

              color: "#fff",
              fontWeight: 600,
              cursor: "pointer",

              transition: "all 0.2s ease",
            }}
          >
            {loading ? "Please wait..." : isSignup ? "Sign Up" : "Login"}
          </button>
        </form>

        {/* MESSAGE */}
        {message && (
          <p
            style={{
              marginTop: 14,
              textAlign: "center",
              color: message.toLowerCase().includes("success")
                ? "#2e7d32"
                : "#c62828",
            }}
          >
            {message}
          </p>
        )}

        {/* TOGGLE */}
        <p style={{ textAlign: "center", marginTop: 20 }}>
          {isSignup ? (
            <>
              Already have an account?{" "}
              <span
                style={{ color: "#1976d2", cursor: "pointer", fontWeight: 600 }}
                onClick={() => {
                  setIsSignup(false);
                  resetForm();
                }}
              >
                Login
              </span>
            </>
          ) : (
            <>
              Don’t have an account?{" "}
              <span
                style={{ color: "#1976d2", cursor: "pointer", fontWeight: 600 }}
                onClick={() => {
                  setIsSignup(true);
                  resetForm();
                }}
              >
                Sign Up
              </span>
            </>
          )}
        </p>
      </div>
    </div>
  );
}

function InputField({ label, name, value, onChange, error }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <input
        name={name}
        value={value}
        onChange={onChange}
        placeholder={`${label} *`}
        style={{
          width: "100%",
          padding: "12px 14px",
          borderRadius: 10,

          border: error
            ? "1px solid #ef4444"
            : "1px solid rgba(255,255,255,0.5)",

          background: "rgba(255,255,255,0.6)",
          backdropFilter: "blur(10px)",

          fontSize: 14,
          outline: "none",
        }}
      />
      {error && <span style={{ color: "#ef4444", fontSize: 12 }}>{error}</span>}
    </div>
  );
}

export default UserAuth;
